import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import Pagination from '../../components/common/Pagination';
import { formatDate } from '../../utils/dateFormat';
import styles from './Expenses.module.css';

const FREQUENCY_OPTIONS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ANNUAL', label: 'Annual' },
];

function FixedExpenseForm({ businesses, expense, onSave, onCancel }) {
  const [businessId, setBusinessId] = useState(expense?.business || '');
  const [name, setName] = useState(expense?.name || '');
  const [amount, setAmount] = useState(expense?.amount || '');
  const [category, setCategory] = useState(expense?.category || '');
  const [frequency, setFrequency] = useState(expense?.frequency || 'MONTHLY');
  const [startDate, setStartDate] = useState(expense?.start_date || '');
  const [endDate, setEndDate] = useState(expense?.end_date || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { business: businessId, name, amount, category, frequency, start_date: startDate, end_date: endDate || null };
      if (expense) {
        await api.put(`/expenses/fixed/${expense.id}/`, payload);
      } else {
        await api.post('/expenses/fixed/', payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save expense.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>{expense ? 'Edit Fixed Expense' : 'Add Fixed Expense'}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          {!expense && (
            <label className={styles.label}>
              Business
              <select value={businessId} onChange={e => setBusinessId(e.target.value)} className={styles.input} required>
                <option value="">Select business...</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className={styles.label}>
            Name
            <input type="text" value={name} onChange={e => setName(e.target.value)} className={styles.input} required />
          </label>
          <div className={styles.row}>
            <label className={styles.label}>
              Amount
              <input type="text" value={amount} onChange={e => setAmount(e.target.value)} className={styles.input} required placeholder="0.00" />
            </label>
            <label className={styles.label}>
              Frequency
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className={styles.input}>
                {FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </label>
          </div>
          <label className={styles.label}>
            Category
            <input type="text" value={category} onChange={e => setCategory(e.target.value)} className={styles.input} required placeholder="e.g. Rent, Utilities" />
          </label>
          <div className={styles.row}>
            <label className={styles.label}>
              Start Date
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={styles.input} required placeholder="dd-mm-yyyy" />
            </label>
            <label className={styles.label}>
              <span>End Date <span className={styles.optional}>(optional)</span></span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={styles.input} min={startDate || undefined} placeholder="dd-mm-yyyy" />
            </label>
          </div>
          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VariableExpenseForm({ businesses, expense, onSave, onCancel }) {
  const [businessId, setBusinessId] = useState(expense?.business || '');
  const [name, setName] = useState(expense?.name || '');
  const [amount, setAmount] = useState(expense?.amount || '');
  const [category, setCategory] = useState(expense?.category || '');
  const [expenseDate, setExpenseDate] = useState(expense?.expense_date || new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState(expense?.notes || '');
  const [frequency, setFrequency] = useState(expense?.frequency || '');
  const [startDate, setStartDate] = useState(expense?.start_date || '');
  const [endDate, setEndDate] = useState(expense?.end_date || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        business: businessId, name, amount, category, expense_date: expenseDate, notes,
        frequency: frequency || '',
        start_date: startDate || null,
        end_date: endDate || null,
      };
      if (expense) {
        await api.put(`/expenses/variable/${expense.id}/`, payload);
      } else {
        await api.post('/expenses/variable/', payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save expense.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>{expense ? 'Edit Variable Expense' : 'Add Variable Expense'}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          {!expense && (
            <label className={styles.label}>
              Business
              <select value={businessId} onChange={e => setBusinessId(e.target.value)} className={styles.input} required>
                <option value="">Select business...</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className={styles.label}>
            Name
            <input type="text" value={name} onChange={e => setName(e.target.value)} className={styles.input} required />
          </label>
          <div className={styles.row}>
            <label className={styles.label}>
              Amount
              <input type="text" value={amount} onChange={e => setAmount(e.target.value)} className={styles.input} required placeholder="0.00" />
            </label>
            <label className={styles.label}>
              Date
              <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} className={styles.input} required placeholder="dd-mm-yyyy" />
            </label>
          </div>
          <label className={styles.label}>
            Category
            <input type="text" value={category} onChange={e => setCategory(e.target.value)} className={styles.input} required placeholder="e.g. Supplies, Marketing" />
          </label>
          <label className={styles.label}>
            Notes
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className={styles.textarea} rows={2} placeholder="Optional" />
          </label>
          <label className={styles.label}>
            <span>Recurrence Frequency <span className={styles.optional}>(optional)</span></span>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className={styles.input}>
              <option value="">None / One-off</option>
              {FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </label>
          {frequency && (
            <div className={styles.row}>
              <label className={styles.label}>
                <span>Recurrence Start <span className={styles.optional}>(optional)</span></span>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={styles.input} placeholder="dd-mm-yyyy" />
              </label>
              <label className={styles.label}>
                <span>Recurrence End <span className={styles.optional}>(optional)</span></span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={styles.input} min={startDate || undefined} placeholder="dd-mm-yyyy" />
              </label>
            </div>
          )}
          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Expenses() {
  const [tab, setTab] = useState('fixed');
  const [fixed, setFixed] = useState([]);
  const [variable, setVariable] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [filterBizId, setFilterBizId] = useState('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fixedRes, varRes, bizRes] = await Promise.all([
        api.get('/expenses/fixed/'),
        api.get('/expenses/variable/'),
        api.get('/businesses/'),
      ]);
      if (fixedRes.data.success) setFixed(fixedRes.data.data || []);
      if (varRes.data.success) setVariable(varRes.data.data || []);
      if (bizRes.data.success) setBusinesses(bizRes.data.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleAdd() {
    setEditExpense(null);
    setShowForm(true);
  }

  async function handleDelete(type, id) {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${type}/${id}/`);
      fetchData();
    } catch { /* empty */ }
  }

  function handleSaved() {
    setShowForm(false);
    setEditExpense(null);
    fetchData();
  }

  const filteredFixed = filterBizId === 'all' ? fixed : fixed.filter(e => e.business === filterBizId);
  const filteredVariable = filterBizId === 'all' ? variable : variable.filter(e => e.business === filterBizId);
  const currentList = tab === 'fixed' ? filteredFixed : filteredVariable;
  const pagedList = currentList.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle="Track fixed and variable expenses"
        actions={
          <button onClick={handleAdd} className={styles.btnPrimary}>+ Add Expense</button>
        }
      />

      {/* Tabs + Business Filter */}
      <div className={styles.tabsRow}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'fixed' ? styles.tabActive : ''}`}
            onClick={() => { setTab('fixed'); setPage(1); }}
          >
            Fixed ({filteredFixed.length})
          </button>
          <button
            className={`${styles.tab} ${tab === 'variable' ? styles.tabActive : ''}`}
            onClick={() => { setTab('variable'); setPage(1); }}
          >
            Variable ({filteredVariable.length})
          </button>
        </div>
        {businesses.length > 1 && (
          <select
            value={filterBizId}
            onChange={e => { setFilterBizId(e.target.value); setPage(1); }}
            className={styles.bizFilter}
          >
            <option value="all">All Businesses</option>
            {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <p className={styles.loadingText}>Loading...</p>
      ) : currentList.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No {tab} expenses yet</h3>
          <p>Add your first {tab} expense to start tracking.</p>
        </div>
      ) : tab === 'fixed' ? (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 52, textAlign: 'center' }}>Sl. No</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Business</th>
                <th>Frequency</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedList.map((e, idx) => (
                <tr key={e.id}>
                  <td style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{(page - 1) * perPage + idx + 1}</td>
                  <td className={styles.nameCell}>{e.name}</td>
                  <td>{e.amount}</td>
                  <td>{e.category}</td>
                  <td className={styles.bizCell}>{e.business_name || '-'}</td>
                  <td>{e.frequency}</td>
                  <td className={styles.dateCell}>{formatDate(e.start_date)}</td>
                  <td className={styles.dateCell}>{formatDate(e.end_date)}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${e.is_active ? styles.active : styles.inactive}`}>
                      {e.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        onClick={() => { setEditExpense(e); setShowForm(true); }}
                        className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                        title="Edit"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete('fixed', e.id)}
                        className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={currentList.length}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={n => { setPerPage(n); setPage(1); }}
          />
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 52, textAlign: 'center' }}>Sl. No</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Business</th>
                <th>Frequency</th>
                <th>Date</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedList.map((e, idx) => (
                <tr key={e.id}>
                  <td style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{(page - 1) * perPage + idx + 1}</td>
                  <td className={styles.nameCell}>{e.name}</td>
                  <td>{e.amount}</td>
                  <td>{e.category}</td>
                  <td className={styles.bizCell}>{e.business_name || '-'}</td>
                  <td>{e.frequency || '-'}</td>
                  <td className={styles.dateCell}>{formatDate(e.expense_date)}</td>
                  <td className={styles.notesCell}>{e.notes || '-'}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        onClick={() => { setTab('variable'); setEditExpense(e); setShowForm(true); }}
                        className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                        title="Edit"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete('variable', e.id)}
                        className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={currentList.length}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={n => { setPerPage(n); setPage(1); }}
          />
        </div>
      )}

      {showForm && tab === 'fixed' && (
        <FixedExpenseForm
          businesses={businesses}
          expense={editExpense}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {showForm && tab === 'variable' && (
        <VariableExpenseForm
          businesses={businesses}
          expense={editExpense}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default Expenses;
